import { z } from "zod";

import { type CreateOnboardingProcessStepDto } from "#modules/onboardingV2/infra/createOnboardingProcessStep.api";
import { euroToCents, parseInputNumberValue } from "#shared/helpers/prices";
import {
  IndustrialProcessSchema,
  IndustrialProcessType,
  resolveOnboardingProcessStepTarget,
  SurfaceTreatmentMethodSchema,
  SurfaceTreatmentTypeSchema,
} from "shared";

import { isValidEuroAmount } from "./isValidEuroAmount";

export const AddProcessStepFormSchema = z
  .object({
    industrialProcess: IndustrialProcessSchema.nullable(),
    type: SurfaceTreatmentTypeSchema,
    machineName: z.string(),
    method: SurfaceTreatmentMethodSchema.nullable(),
    price: z.string(),
    preparationTimeFormula: z.string().nullable(),
    cycleTimeFormula: z.string().nullable(),
  })
  .superRefine((values, ctx) => {
    if (!values.industrialProcess) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["industrialProcess"] });
    }

    const target = resolveOnboardingProcessStepTarget(values.industrialProcess, values.type);
    const isSurfaceTreatmentTarget = target === "SURFACE_TREATMENT";

    if (!isSurfaceTreatmentTarget) {
      return;
    }

    if (!values.method) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["method"] });
    }
    if (!isValidEuroAmount(values.price)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["price"] });
    }
  })
  .transform(
    (values): CreateOnboardingProcessStepDto => ({
      industrialProcess: values.industrialProcess as IndustrialProcessType,
      type: values.type,
      machineName: values.machineName.trim() || undefined,
      method: values.method ?? SurfaceTreatmentMethodSchema.enum.time,
      price: isValidEuroAmount(values.price)
        ? euroToCents(parseInputNumberValue(values.price))
        : undefined,
      preparationTimeFormula: values.preparationTimeFormula,
      cycleTimeFormula: values.cycleTimeFormula,
    }),
  );

export type AddProcessStepFormValues = z.input<typeof AddProcessStepFormSchema>;
